/*!
 * Copyright 2017 - 2020 by ChartIQ, Inc.
 * All rights reserved.
 */
import { BaseService } from "../baseService";
import { RouterClient, ResponderMessage } from "../../clients/routerClient";
import { ConfigClient } from "../../clients/configClient";
import { ConfigUtilInstance as ConfigUtil } from "../../common/configUtil";
import { Logger } from "../../clients/logger";
import SystemManagerClient from "../../common/systemManagerClient";
// The adapter receiver is an object that allows dynamically loaded storage adapters to register their existence with the storageService.
import AdapterReceiver from "./AdapterReceiver";
import { StandardError } from "../../types";
import { parallel as asyncParallel } from "async";

/**
 * A custom timeout which features a 'cleared' flag
 * to indicate a timer has run
 */
export class Timeout {
	fired: boolean;

	id: any;

	/**
	 * Constructs a new Timeout
	 * @param {Function} fn The function to run when the timer is reached
	 * @param {number} interval The number of milliseconds until the function runs
	 */
	constructor(fn, interval) {
		this.fired = false;
		this.id = setTimeout(() => {
			// Run the function
			fn();

			// Indicate that the timer has been run
			this.fired = true;
		}, interval);
		this.clear = this.clear.bind(this);
		this.didFire = this.didFire.bind(this);
	}

	clear() {
		if (!this.fired) clearTimeout(this.id);
	}

	didFire() {
		return this.fired;
	}
}

Logger.start();

// Fire notifications when storage adapter operations take longer than this timeout (in ms)
const ADAPTER_OPERATION_TIMEOUT_DEFAULT = 10000;
const ADAPTER_NOTIFICATION_THROTTLE = 3 * 60000;
const Receiver = new AdapterReceiver();

// By setting adapterReceiver on the global object, the incoming storage model can call methods on it (e.g., adapterReceiver.loaded).
(global as any).adapterReceiver = Receiver;
(global as any).storageAdapters = Receiver.adapters;

type StorageData = Record<string, any>;
type StorageAdapter = Record<string, any>;

let debuggerInfo: {
	help?: Function;
	setRandomDelayBounds?: Function;
	setErrorMode?: Function;
	clearErrorMode?: Function;
} = window as any;

/**
 * The storage service is the receiver for calls from the storageClient. It routes calls to the appropriate storage adapters and handle the dynamic loading of those adapters.
 */
class StorageService extends BaseService {
	user: string;

	storageAdapters: Record<string, StorageAdapter>;

	currentDefaultDataStore: string | null;

	topicToStorageAdapterMap: Record<string, string>;

	lastNotificationForErrType: Record<string, string>;

	adapterOperationTimeout: number;

	notificationThrottle: number;

	storageAdaptersInConfig: Record<string, any>;

	serviceReady: boolean;

	constructor() {
		super({
			name: "storageService",
			startupDependencies: {},
			shutdownDependencies: {
				services: ["windowService"],
			},
		});
		this.storageAdapters = {};
		this.user = "default";
		this.currentDefaultDataStore = null;
		this.topicToStorageAdapterMap = {};
		this.lastNotificationForErrType = {};
		this.adapterOperationTimeout = 0;
		this.notificationThrottle = 0;
		this.storageAdaptersInConfig = {};
		this.serviceReady = false;
	}

	/**
	 * Gets pertinent info from config. Tells us which topics map to which storage adapters, and kicks off the loading of the storage adapters.
	 * @memberof StorageService
	 * @param {cb} Callback to be called when initialization is complete. At that point, the service is set online.
	 */
	async initialize(cb: Function): Promise<void> {
		try {
			Logger.system.info("StorageService.initialize", "Start");
			// baseService method
			await this.getConfig();
			await this.fetchAdapters();

			// when all adapters are fetched and initialized, load them into the service
			this.loadAdapters();
			// storage adapters are not fully ready to be used until after authentication because they require the username
			SystemManagerClient.waitForBootStage("system-preuser", "stageEntered", async () => {
				const result = await this.testAdapters();
				if (result !== "success") {
					SystemManagerClient.systemLog({ error: true }, `StorageService.testAdapters() failed: ${result}`);
				}
			});
			this.createRouterEndpoints();
			this.serviceReady = true;
			this.help();
			cb?.();
		} catch (err) {
			Logger.system.error(`APPLICATION LIFECYCLE: ${err}`);
			console.error(err);
		}
	}

	/**
	 * Retrieves essential config for the service (e.g., which adapters it should load into the storage service.)
	 */
	async getConfig(): Promise<void> {
		let storageConfig;
		storageConfig = await ConfigClient.getValue({
			field: "finsemble.servicesConfig.storage",
		}).catch(() => {
			Logger.system.error("APPLICATION LIFECYCLE: Storage config not found in finsemble.servicesConfig.storage");
		});
		if (storageConfig && !storageConfig.data) {
			console.log("Checking finsemble.storage");
			storageConfig = await ConfigClient.getValue({
				field: "finsemble.storage",
			}).catch(() => {
				Logger.system.error("APPLICATION LIFECYCLE: Storage config not found in finsemble.storage");
			});

			// Warn about deprecated config location
			if (storageConfig && storageConfig.data) {
				Logger.system.deprecated(
					"Please move storage config to servicesConfig.storage. This location of config for storage is deprecated"
				);
			}
		}
		if (storageConfig && !storageConfig.data) return Promise.reject("Cannot find config");

		// If storage config was found in either of the locations checked and has a value
		// the actual value is stored in .data
		storageConfig = storageConfig?.data;

		let { defaultStorage } = storageConfig;
		if (!defaultStorage) {
			// Backwards compatibility where defaultStorage was stored at the finsemble level
			defaultStorage = await ConfigClient.getValue({
				field: "finsemble.defaultStorage",
			});
			Logger.system.deprecated(
				"Please move defaultStorage config to servicesConfig.storage. This location of config for defaultStorage is deprecated"
			);
			defaultStorage = defaultStorage.data;
		}
		if (!defaultStorage) {
			Logger.system.warn("Default storage not found, defaulting to localStorage");
		}
		defaultStorage = defaultStorage || "localStorage";

		this.setStore({ dataStore: defaultStorage });
		Logger.system.debug(
			"APPLICATION LIFECYCLE: StorageService.initialize",
			`Setting default storage to ${defaultStorage}`
		);

		// Get stores to use by topic if provided.
		if (storageConfig.topicToDataStoreAdapters) {
			const topics = Object.keys(storageConfig.topicToDataStoreAdapters);
			for (let i = 0; i < topics.length; i++) {
				const thisTopic = topics[i];
				const thisStore = storageConfig.topicToDataStoreAdapters[thisTopic];
				Logger.system.log(
					"APPLICATION LIFECYCLE: StorageService.initialize",
					"topic mapping",
					`Setting store for topic ${thisTopic} to store ${thisStore}`
				);
				this.setStore({ topic: thisTopic, dataStore: thisStore });
			}
		}

		this.adapterOperationTimeout = ConfigUtil.getDefault(
			storageConfig,
			"storageConfig.adapterOperationTimeout",
			ADAPTER_OPERATION_TIMEOUT_DEFAULT
		);
		this.notificationThrottle = ConfigUtil.getDefault(
			storageConfig,
			"storageConfig.notificationThrottle",
			ADAPTER_NOTIFICATION_THROTTLE
		);

		this.storageAdaptersInConfig = storageConfig.dataStoreAdapters || storageConfig; // Not sure why this is here. Likely for backward compatibility.

		return Promise.resolve();
	}

	/**
	 * Does the dirty work of firing off network requests and waiting for the adapters to let us know that they're loaded and ready. When all adapters are finished loading, this function returns.
	 */
	async fetchAdapters(): Promise<unknown> {
		const storageAdapters = this.storageAdaptersInConfig;
		// If there's no storageAdapters
		if (!storageAdapters) {
			Logger.system.log(
				"APPLICATION LIFECYCLE: StorageService.initialize",
				"finish. No storage adapters included in config."
			);
			return Promise.resolve();
		}

		const asyncMethods: Function[] = [];
		// Here we create an async task that loads each storage adapter. When the adapter has completed loading, the done callback is invoked. We know that the adapter is loaded because the baseAdapter calls Receiver.loaded.
		for (const key in storageAdapters) {
			const fetchAdapter = function (done: Function) {
				Logger.system.log("APPLICATION LIFECYCLE: StorageService.initialize", `Load adapter:${key}:start`);
				Receiver.loadModel(key, storageAdapters[key], () => {
					Logger.system.log("APPLICATION LIFECYCLE: StorageService.initialize", `Load adapter:${key}:finish`);
					done();
				});
			};
			asyncMethods.push(fetchAdapter as never);
		}

		const doFetch = (resolve: Function, reject: Function) => {
			function onFetchingCompleted(err: StandardError) {
				Logger.system.info("StorageService.initialize", "finish");
				if (err) {
					Logger.system.error(err);
					return reject(err);
				}
				return resolve();
			}

			asyncParallel(asyncMethods, onFetchingCompleted);
		};

		return new Promise(doFetch);
	}

	/**
	 * Creates the public interface with the rest of the application.
	 * Each of the handlers below simply receive a request, pass it off to a member function that does the work, and then sends the result back to the function that sent the original message.
	 * @memberof StorageService
	 */
	createRouterEndpoints(): void {
		RouterClient.addResponder("Storage.clearCache", (err1, message) => {
			if (message) {
				Logger.system.info("StorageService.clearCache", message);
				this.clearCache(message, (err, data) => {
					message.sendQueryResponse(err, data);
				});
			} else {
				this.logNoMessageError("StorageService.clearCache - Storage.clearCache");
			}
		});

		RouterClient.addResponder("Storage.delete", (err1, message) => {
			if (message) {
				Logger.system.info("StorageService.delete", message);
				this.delete(message, (err, data) => {
					message.sendQueryResponse(err, data);
				});
			} else {
				this.logNoMessageError("StorageService.delete - Storage.delete");
			}
		});

		RouterClient.addResponder("Storage.get", (err1, message) => {
			if (message) {
				const startTime = performance.now();
				Logger.system.info("StorageService.get", message);
				this.get(message, (err, data) => {
					Logger.system.debug("StorageService.get returned after milliseconds:", performance.now() - startTime, data);
					message.sendQueryResponse(err, data);
				});
			} else {
				this.logNoMessageError("StorageService.get - Storage.get");
			}
		});

		RouterClient.addResponder("Storage.getMultiple", (err, message) => {
			if (message) {
				Logger.system.info("StorageService.getMultiple", message);
				message.sendQueryResponse(err, "get multiple is not implemented");
			} else {
				this.logNoMessageError("StorageService.getMultiple - Storage.getMultiple");
			}
		});

		RouterClient.addResponder("Storage.keys", (err1, message) => {
			if (message) {
				Logger.system.info("StorageService.keys", message);
				this.keys(message, (err, data) => {
					message.sendQueryResponse(err, data);
				});
			} else {
				this.logNoMessageError("StorageService.keys - Storage.keys");
			}
		});

		RouterClient.addResponder("Storage.save", (err1, message) => {
			if (message) {
				Logger.system.info("StorageService.save", message);
				this.save(message, (err, data) => {
					message?.sendQueryResponse(err, data);
				});
			} else {
				this.logNoMessageError("StorageService.save - Storage.save");
			}
		});

		RouterClient.addResponder("Storage.setStore", (err, message) => {
			if (message) {
				Logger.system.info("StorageService.setStore", message);
				this.setStore(message.data);
				message.sendQueryResponse(null, { status: "success" });
			} else {
				this.logNoMessageError("StorageService.setStore - Storage.setStore");
			}
		});

		RouterClient.addResponder("Storage.setUser", (err1, message) => {
			if (message) {
				Logger.system.info("StorageService.setUser", message);
				this.setUser(message, (err, data) => {
					message.sendQueryResponse(err, data);
				});
			} else {
				this.logNoMessageError("StorageService.setUser - Storage.setUser");
			}
		});
	}

	private logNoMessageError(responder: string) {
		Logger.system.error(`Missing message for responder ${responder}`);
	}

	/**
	 * Logs a storage adapter error.
	 *
	 * @memberof StorageService
	 * @param {string} msg Error message to display in notification and log.
	 * @param {object} data Additional data object to render in log.
	 * @private
	 */
	notifyError(msg: string, data: StorageData | null): void {
		Logger.system.error(msg, data);
	}

	/**
	 * Utility function to set up a custom timeout indicating a response which took too long.
	 * Sends the callback to the invoking function indicating the timeout.
	 *
	 * @memberof StorageService
	 * @param {string} operation The name of the operation.
	 * @param {string} dataStore The name of the data store being used.
	 * @param {*} data Additional data to append to the log message/response.
	 * @param {*} cb The callback from the invoker. A timeout will cause an error callback.
	 * @returns {*} Timeout
	 * @private
	 */
	createTimeoutResponder(operation: string, dataStore: string, data: StorageData, cb: Function): Timeout {
		return new Timeout(() => {
			const msg = `Warning! StorageService.${operation}: Storage adapter '${dataStore}' is taking too long to respond`;
			if (!data) data = {};
			this.notifyError(msg, data);
			if (cb) {
				cb(msg, data);
			}
		}, this.adapterOperationTimeout);
	}

	/**
	 * Utility function to check if a response contains an error to log.
	 *
	 * @memberof StorageService
	 * @param {string} operation The name of the operation.
	 * @param {string} dataStore The name of the data store being used.
	 * @param {*} err The error object to test. Falsy values are ignored.
	 * @param {Timeout} timeout Custom timeout containing a `setTimeout` to be cleared when output is checked for errors.
	 * @param {Object} data Additional data to be logged on error.
	 * @param {cb} [cb] Callback to be called after checking for errors to log.
	 */
	clearTimerAndLogIfError(
		operation: string,
		dataStore: string,
		err: StandardError,
		timeout: Timeout | null,
		data: StorageData | null,
		cb: (err: StandardError, msg: Record<string, any> | null) => void
	): void {
		if (err) {
			const msg = `StorageService.${operation}: Error in Storage adapter '${dataStore}'`;
			this.notifyError(msg, data);
		}
		if (timeout) timeout.clear();
		if (cb) cb(err, data);
	}

	/**
	 * Calls the clear cache method for the storage adapter for a particular data type.
	 * @memberof StorageService
	 * @param {routerQuery} message
	 * @param cb  (optional) Called on completion.
	 */
	clearCache(message: ResponderMessage<any>, cb: (err: StandardError, data: Record<string, any> | null) => void): void {
		const dataStore = this.getStore(message.data, "clearCache", cb);
		if (!dataStore || !this.storageAdapters[dataStore]) return; // callback will already have been called by getStore if this is the case
		const timeout = this.createTimeoutResponder("clearCache", dataStore, message.data, cb);
		try {
			this.storageAdapters[dataStore].clearCache(message.data, (err: StandardError, data: Record<string, any>) => {
				this.clearTimerAndLogIfError(
					"clearCache",
					dataStore,
					err,
					timeout,
					data,
					(successCheckErrErr, successCheckErrRes) => {
						if (!timeout.didFire()) cb(successCheckErrErr, successCheckErrRes);
					}
				);
			});
		} catch (err) {
			this.clearTimerAndLogIfError(
				"clearCache",
				dataStore,
				err as StandardError,
				timeout,
				null,
				(errCheckErrorErr, errCheckErrorRes) => {
					if (!timeout.didFire()) cb(errCheckErrorErr, errCheckErrorRes);
				}
			);
		}
	}

	/**
	 * Deletes a piece of data.
	 * @memberof StorageService
	 * @param {routerQuery} message
	 * @param cb  (optional) Called on completion.
	 */
	delete(message: ResponderMessage<any>, cb: (err: StandardError, data: Record<string, any> | null) => void): void {
		this.serviceReady || this.notifyError("storageService invoked before ready", message);
		if (!message || !message.data || !message.data.key) {
			const msg = `StorageService.delete: No storage key found in message data, message: '${message}'`;
			this.notifyError(msg, {});
			cb(msg, null);
			return;
		}
		const dataStore = this.getStore(message.data, "delete", cb);
		if (!dataStore || !this.storageAdapters[dataStore]) return; // callback will already have been called by getStore if this is the case
		const timeout = this.createTimeoutResponder("delete", dataStore, message.data, cb);
		try {
			this.storageAdapters[dataStore].delete(message.data, (err: StandardError, data: Record<string, any>) => {
				this.clearTimerAndLogIfError(
					"delete",
					dataStore,
					err,
					timeout,
					data,
					(successCheckErrErr, successCheckErrRes) => {
						if (!timeout.didFire()) cb(successCheckErrErr, successCheckErrRes);
					}
				);
			});
		} catch (err) {
			this.clearTimerAndLogIfError(
				"delete",
				dataStore,
				err as StandardError,
				timeout,
				null,
				(errCheckErrorErr, errCheckErrorRes) => {
					if (!timeout.didFire()) cb(errCheckErrorErr, errCheckErrorRes);
				}
			);
		}
	}

	/**
	 * Retrieves a piece of data.
	 * @memberof StorageService
	 * @param {routerQuery} message
	 * @param cb  (optional) Called on completion.
	 */
	get(message: StorageData, cb: (err: StandardError, data: Record<string, any> | null) => void): void {
		this.serviceReady || this.notifyError("storageService invoked before ready", message);
		if (!message || !message.data || !message.data.key) {
			const msg = `StorageService.get: No storage key found in message data, message: '${message}'`;
			this.notifyError(msg, {});
			cb(msg, null);
			return;
		}
		const { data } = message;
		const dataStore = this.getStore(data, "get", cb);
		if (!dataStore || !this.storageAdapters[dataStore]) return; // callback will already have been called by getStore if this is the case
		const timeout = this.createTimeoutResponder("get", dataStore, data, cb);
		try {
			this.storageAdapters[dataStore].get(data, (err: StandardError, returnedData: Record<string, any>) => {
				this.clearTimerAndLogIfError(
					"get",
					dataStore,
					err,
					timeout,
					returnedData,
					(successCheckErrErr, successCheckErrRes) => {
						if (!timeout.didFire()) cb(successCheckErrErr, successCheckErrRes);
					}
				);
			});
		} catch (err) {
			this.clearTimerAndLogIfError(
				"get",
				dataStore,
				err as StandardError,
				timeout,
				null,
				(errCheckErrorErr, errCheckErrorRes) => {
					if (!timeout.didFire()) cb(errCheckErrorErr, errCheckErrorRes);
				}
			);
		}
	}

	/**
	 * Returns all of the keys for a particular storage adapter.
	 * @memberof StorageService
	 * @param {routerQuery} message
	 * @param cb  (optional) Called on completion.
	 */
	keys(message: StorageData, cb: (err: StandardError, data: Record<string, any> | null) => void): void {
		this.serviceReady || this.notifyError("storageService invoked before ready", message);
		if (!message || !message.data) {
			const msg = `StorageService.keys: No message data passed to function, message: '${message}'`;
			this.notifyError(msg, {});
			cb(msg, null);
			return;
		}
		const dataStore = this.getStore(message.data, "keys", cb);
		if (!dataStore || !this.storageAdapters[dataStore]) return; // callback will already have been called by getStore if this is the case
		const timeout = this.createTimeoutResponder("keys", dataStore, message.data, cb);
		try {
			this.storageAdapters[dataStore].keys(message.data, (err: StandardError, data: Record<string, any>) => {
				this.clearTimerAndLogIfError(
					"keys",
					dataStore,
					err,
					timeout,
					data,
					(successCheckErrErr, successCheckErrRes) => {
						if (!timeout.didFire()) cb(successCheckErrErr, successCheckErrRes);
					}
				);
			});
		} catch (err) {
			this.clearTimerAndLogIfError(
				"keys",
				dataStore,
				err as StandardError,
				timeout,
				null,
				(errCheckErrorErr, errCheckErrorRes) => {
					if (!timeout.didFire()) cb(errCheckErrorErr, errCheckErrorRes);
				}
			);
		}
	}

	/**
	 * Iterates through all of the loaded storage adapters and gives the StorageService an internal reference.
	 * @memberof StorageService
	 * @param {routerQuery} message
	 */
	loadAdapters(): void {
		const adapters = Receiver.storageAdapters;
		for (const modelKey in adapters) {
			if (this.storageAdapters && !this.storageAdapters[modelKey]) {
				/**
				 * Storage adapter's "combined key" may be prefixed with a "baseName" that can be
				 * used to differentiate storage items when running the same Finsemble instance
				 * across multiple environments. "startup_app.uuid" from Finsemble's manifest
				 * is used for this purpose (the manifest file used is configured via the serverConfig
				 * option in server-environment-startup.json).
				 */
				adapters[modelKey].setBaseName(this.customData.manifest.startup_app?.uuid ?? "Finsemble");
				adapters[modelKey].setUser(this.user);
				this.storageAdapters[modelKey] = adapters[modelKey];
			}
		}
	}

	/**
	 * Interates through all of the adapters and checks to see if data can be fetched
	 * @memberof StorageService
	 */
	testAdapters(): Promise<string> {
		return new Promise(async (resolve, reject) => {
			const topics = this.topicToStorageAdapterMap;
			const adapters = Receiver.storageAdapters;
			let promiseArr: Promise<any>[] = [];
			for (let i = 0; i < Object.keys(topics).length; i++) {
				const topic = Object.keys(topics)[i];
				const thisTopic = topics[topic];

				const testPromise = function (): Promise<any> {
					return new Promise((res, rej) => {
						adapters[thisTopic].keys({ topic }, (err, resp) => {
							const promiseTopic = topic;
							if (resp?.status == "failed") {
								rej(`storageService failed for ${promiseTopic}`);
							} else {
								res("success");
							}
						});
					});
				};
				promiseArr.push(testPromise());
			}
			try {
				await Promise.all(promiseArr);
				resolve("success");
			} catch (err) {
				reject(err);
			}
		});
	}

	/**
	 * Saves some data.
	 * @memberof StorageService
	 * @param {routerQuery} message data to be stored.
	 * @param cb  (optional) Called on completion.
	 */
	save(message: StorageData, cb: (err: StandardError, data: Record<string, any> | null) => void): void {
		this.serviceReady || this.notifyError("storageService invoked before ready", message);
		if (!message || !message.data || !message.data.key) {
			const msg = `StorageService.save: No storage key found in message data, message: '${message}'`;
			this.notifyError(msg, {});
			cb(msg, null);
			return;
		}
		if (!message.data.value === undefined) {
			const msg = `StorageService.save: No value found to store in message data, message: '${message}'`;
			this.notifyError(msg, {});
			cb(msg, null);
			return;
		}

		const dataStore = this.getStore(message.data, "save", cb);
		if (!dataStore || !this.storageAdapters[dataStore]) return; // callback will already have been called by getStore if this is the case
		const timeout = this.createTimeoutResponder("save", dataStore, message.data, cb);
		try {
			this.storageAdapters[dataStore].save(message.data, (err: StandardError, data: Record<string, any>) => {
				this.clearTimerAndLogIfError(
					"save",
					dataStore,
					err,
					timeout,
					data,
					(successCheckErrErr, successCheckErrRes) => {
						if (!timeout.didFire()) cb(successCheckErrErr, successCheckErrRes);
					}
				);
			});
		} catch (err) {
			this.clearTimerAndLogIfError(
				"save",
				dataStore,
				err as StandardError,
				timeout,
				null,
				(errCheckErrorErr, errCheckErrorRes) => {
					if (!timeout.didFire()) cb(errCheckErrorErr, errCheckErrorRes);
				}
			);
		}
	}

	/**
	 * Sets the user for the application. Used to namespace stored information.
	 * @memberof StorageService
	 * @param {routerQuery} message
	 */
	setUser(message: StorageData, cb: (err: StandardError, data: StorageData) => void): void {
		this.serviceReady || this.notifyError("storageService invoked before ready", message);
		this.user = message.data.user;
		for (const modelKey in this.storageAdapters) {
			Logger.system.log(`Model: ${modelKey} User: ${JSON.stringify(message.data.user)}`);
			this.storageAdapters[modelKey].setUser(message.data.user);
		}
		cb(null, { status: "success" });
	}

	/**
	 *
	 * Retrieves the storage model being used to store a particular topic.
	 * @memberof StorageService
	 * @param {object} data
	 * @param operation The name of the operation to be performed on the store.
	 * @param cb (optional) Callback to be called if an error occurs.
	 * @returns The data store name or null if an error occurs.
	 * @private
	 */
	getStore(
		data: StorageData,
		operation: string,
		cb: (err: StandardError, data: Record<string, any> | null) => void
	): string | null {
		let dataStore: string = this.currentDefaultDataStore || "";
		if (data && data.topic) {
			const topicDataStore = this.topicToStorageAdapterMap[data.topic];
			if (topicDataStore) {
				Logger.system.debug("getStore for topic", data);
				dataStore = topicDataStore;
			} else {
				Logger.system.info(
					`getStore: no data store for topic '${data.topic}', using default data store`,
					data,
					this.topicToStorageAdapterMap[data.topic]
				);
			}
		} else {
			Logger.system.log("getStore: no topic provided, using default data store", dataStore);
		}

		if (!dataStore || !this.storageAdapters[dataStore]) {
			this.clearTimerAndLogIfError(operation, dataStore, `storage adapter '${dataStore}' not found`, null, null, cb);
			return null;
		}

		return dataStore;
	}

	/**
	 * Sets the name of the storage storage model for a particular topic. If the topic is defined, set the store for that topic.
	 * Otherwise, make that topic map to the default data store.
	 * @memberof StorageService
	 * @param {object} data
	 */
	setStore(data: StorageData): void {
		if (data.topic) {
			Logger.system.debug("setStore for topic ${data.topic}", data);
			this.topicToStorageAdapterMap[data.topic] = data.dataStore;
		} else {
			Logger.system.info("setStore for default data store", data);
			this.currentDefaultDataStore = data.dataStore;
		}
	}

	/**
	 * If any of the adapters have the instrumentation function setRandomDelayBounds, then setup instrumentation/testing options and display help instructions.
	 * Note: QA uses instrumented adapte, InstrumentedIndexedDBAdapter, to simulate remote storage errors.
	 *
	 * @memberof StorageService
	 */
	help(): void {
		// list out instrumentation functions
		console.log(
			"***** The following list of instrumentation function can be invoked directly from the console. NOTE ALL PERSISTS ACROSS RESTARTS *****"
		);
		console.log(
			"setRandomDelayBounds(lowerBoundInMilliseconds, upperBoundInMilliseconds) to randomly add delays (between specified bounds) to get/save/delete storage operations - persists across restarts so reset to 0,0 to stop"
		);
		console.log("setErrorMode(1) to always immediately return error for get/save/delete storage operations");
		console.log(
			"setErrorMode(2) to never return a response for get/save/delete storage operations (forcing a higher level timeout)"
		);
		console.log("clearErrorMode() to clear error mode");
		const adapterNames = Object.keys(this.storageAdapters);
		for (let i = 0; i < adapterNames.length; i++) {
			if (this.storageAdapters[adapterNames[i]].setRandomDelayBounds) {
				// make all instrumentation functions easily accessible in main window from console
				debuggerInfo.help = this.help.bind(this);
				debuggerInfo.setRandomDelayBounds = this.storageAdapters[adapterNames[i]].setRandomDelayBounds.bind(
					this.storageAdapters[adapterNames[i]]
				);
				debuggerInfo.setErrorMode = this.storageAdapters[adapterNames[i]].setErrorMode.bind(
					this.storageAdapters[adapterNames[i]]
				);
				debuggerInfo.clearErrorMode = this.storageAdapters[adapterNames[i]].clearErrorMode.bind(
					this.storageAdapters[adapterNames[i]]
				);
				break;
			}
		}
	}
}

export const serviceInstance = new StorageService();
